<template>
  <v-form ref="form" class="contact-form-fields" v-if="fieldsLoaded">
    <form-field
      v-for="fieldName in displayedMainFields"
      :key="fieldName"
      class="mb-3"
      v-model="contact[fieldName]"
      :schema="generateFieldSchema(fieldName)"
      :field="fieldName"
    />

    <div v-if="shouldShowSections" class="additional-fields">
      <div
        v-for="(section, idx) in organizedAdditionalFields"
        :key="`${section.title}-${idx}`"
        class="fields-section"
      >
        <div
          class="fields-section__header"
          @click="toggleSection(section.sectionKey || section.title)"
        >
          <v-icon
            class="fields-section__expand-icon"
            color="appMain"
            :class="{
              'fields-section__expand-icon--expanded': isSectionExpanded(
                section.sectionKey || section.title
              ),
            }"
          >
            mdi-chevron-right
          </v-icon>
          <h4 class="fields-section__title">{{ section.title }}</h4>
        </div>

        <v-expand-transition>
          <div
            v-show="isSectionExpanded(section.sectionKey || section.title)"
            class="field-rows-group"
          >
            <form-field
              v-for="field in section.fields"
              :key="field.name"
              class="mb-3"
              v-model="contact[field.name]"
              :schema="generateFieldSchema(field.name)"
              :field="field.name"
            />
          </div>
        </v-expand-transition>
      </div>
    </div>
  </v-form>
</template>

<script>
import { mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import FormField from '@/components/formFields/FormField';

const MAIN_FIELD_NAMES = ['first_name', 'last_name', 'email', 'mobile'];
const FORBIDDEN_FIELD_TYPES = ['secret'];
const SECTION_STATE_KEY = 'create-contact-sections';

export default {
  name: 'ContactFormFields',

  components: {
    FormField,
  },

  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    editableFields: {
      type: Array,
      default: () => [],
    },
    // Optional map of field name -> value to restore when field is blurred empty (e.g. portal relation default)
    restoreEmptyValueByField: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      expandedSections: this.loadSavedExpandedState(),
    };
  },

  computed: {
    ...mapGetters(['profilePid']),
    ...mapGetters('fields', {
      defaultFields: 'defaultFields',
      getFieldSchema: 'getFieldSchema',
      fieldsLoaded: 'loaded',
    }),
    ...mapGetters('teams', ['currentTeam']),
    ...mapGetters('org', ['fieldSections']),

    // Determines whether to use flat display mode (all fields without sections)
    useFlatDisplay() {
      return this.editableFields.length > 0;
    },

    // Fields that should be visible based on team settings
    visibleFieldNames() {
      const teamFields = this.currentTeam?.contact_fields || [];
      if (!teamFields.length) return [];
      return [...MAIN_FIELD_NAMES, ...teamFields];
    },

    // Fields to display at the top level (either all fields or just main fields)
    displayedMainFields() {
      if (this.useFlatDisplay) {
        // Show all non-hidden fields in a flat list
        return this.getSortedVisibleFields().map((field) => field.name);
      }
      // Show only the standard main fields
      return MAIN_FIELD_NAMES;
    },

    // Additional fields organized by sections (only used when not in flat display mode)
    organizedAdditionalFields() {
      if (!this.defaultFields || !Array.isArray(this.defaultFields)) {
        return [];
      }

      const additionalFields = this.defaultFields.filter(
        (field) =>
          !MAIN_FIELD_NAMES.includes(field.name) && !this.isFieldHidden(field)
      );

      return this.groupFieldsBySection(additionalFields);
    },

    shouldShowSections() {
      return (
        !this.profilePid &&
        this.organizedAdditionalFields.length > 0 &&
        !this.useFlatDisplay
      );
    },
  },

  methods: {
    // ========== Field Filtering & Sorting ==========

    getSortedVisibleFields() {
      if (!this.defaultFields || !Array.isArray(this.defaultFields)) {
        return [];
      }

      const visibleFields = this.defaultFields.filter(
        (field) => !this.isFieldHidden(field)
      );
      return this.sortFields(visibleFields);
    },

    isFieldHidden(field) {
      // Non-editable fields are always hidden
      if (!field.editable) return true;

      // Certain field types are not supported in forms
      if (FORBIDDEN_FIELD_TYPES.includes(field.type)) return true;

      // Form-hidden fields should be hidden
      if (field.form_hidden) return true;

      // If editableFields is specified, only show fields in that list
      if (
        this.editableFields.length &&
        !this.editableFields.includes(field.name)
      ) {
        return true;
      }

      // If team has specific visible fields configured, hide others
      if (
        this.visibleFieldNames.length &&
        !this.visibleFieldNames.includes(field.name)
      ) {
        return true;
      }

      return false;
    },

    sortFields(fields) {
      if (this.visibleFieldNames.length) {
        // Sort by team's field order
        return [...fields].sort(
          (a, b) =>
            this.visibleFieldNames.indexOf(a.name) -
            this.visibleFieldNames.indexOf(b.name)
        );
      }
      // Sort by field's order property
      return [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    // ========== Field Grouping & Section Management ==========

    groupFieldsBySection(fields) {
      // Group fields by section key
      const groupedBySectionKey = fields.reduce((acc, field) => {
        const sectionKey = field.section || 'default';
        if (!acc[sectionKey]) acc[sectionKey] = [];
        acc[sectionKey].push(field);
        return acc;
      }, {});

      // Convert to array of section objects and sort
      return Object.entries(groupedBySectionKey)
        .map(([sectionKey, fields]) => ({
          title: this.getSectionTitle(sectionKey),
          fields: this.sortFields(fields),
          sectionKey,
        }))
        .sort(
          (a, b) =>
            this.getSectionIndex(a.sectionKey) -
            this.getSectionIndex(b.sectionKey)
        );
    },

    getSectionTitle(sectionKey) {
      if (sectionKey === 'default' || !sectionKey) {
        return 'Default';
      }
      const section = this.fieldSections.find(
        ({ slug }) => slug === sectionKey
      );
      return section?.title || sectionKey;
    },

    getSectionIndex(sectionKey) {
      if (sectionKey === 'default' || !sectionKey) {
        return 0;
      }
      const index = this.fieldSections.findIndex(
        ({ slug }) => slug === sectionKey
      );
      return index !== -1 ? index : 999; // Put unknown sections at the end
    },

    // ========== Section Expansion State ==========

    loadSavedExpandedState() {
      const saved = localStorage.getItem(SECTION_STATE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.warn('Failed to parse saved section state:', e);
        }
      }
      // Default: expand the first section
      return { default: true };
    },

    toggleSection(sectionKey) {
      this.expandedSections = {
        ...this.expandedSections,
        [sectionKey]: !this.expandedSections[sectionKey],
      };
      this.saveExpandedState();
    },

    isSectionExpanded(sectionKey) {
      return !!this.expandedSections[sectionKey];
    },

    saveExpandedState() {
      localStorage.setItem(
        SECTION_STATE_KEY,
        JSON.stringify(this.expandedSections)
      );
    },

    // ========== Field Schema ==========

    generateFieldSchema(fieldName) {
      const fieldSchema = cloneDeep(this.getFieldSchema(fieldName));

      if (!fieldSchema) {
        console.warn(`Field schema not found for: ${fieldName}`);
        return {};
      }

      const restoreEmptyValue = this.restoreEmptyValueByField[fieldName];
      return {
        ...fieldSchema,
        uploadOnly: fieldSchema.type === 'Image',
        ...(fieldSchema.type === 'DateTime' && { noDefault: true }),
        ...(restoreEmptyValue && { restoreEmptyValue }),
        ...(fieldSchema.type === 'Date' && { hide_header: true }),
      };
    },

    // ========== Public API ==========

    validate() {
      return this.$refs.form.validate();
    },

    reset() {
      return this.$refs.form.reset();
    },
  },
};
</script>

<style lang="less">
.contact-form-fields {
  .field-block + .field-block {
    margin-top: 20px;
  }

  .placeholder-block {
    background-color: var(--v-lightGray-base);
  }

  .fields-section {
    margin: 16px 0;
    border: 1px solid var(--v-borderColor-base);
    border-radius: @base-radius;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &__header {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: 16px 12px;
      user-select: none;
      transition: background-color 0.2s ease;
      border-radius: @base-radius @base-radius 0 0;

      &:hover {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }

    &__expand-icon {
      margin-right: 8px;
      transition: transform 0.2s ease;
      color: var(--v-textSecondary-base);

      &--expanded {
        transform: rotate(90deg);
      }
    }

    &__title {
      font-weight: 600;
      font-size: 16px;
      color: var(--v-appMain-base);
      margin: 0;
      flex: 1;
      text-transform: capitalize;
    }

    .field-rows-group {
      border-top: 2px solid var(--v-borderColor-base);
      background: var(--v-lightGray-base);
      padding: 16px 12px;
      border-radius: 0 0 @base-radius @base-radius;
    }
  }
}
</style>
